import { useState, useRef } from "react";
import styles from "./BlacklistRow.module.scss";
import tableStyles from "./BlacklistTable.module.scss";
import { MoreOptionsIcon } from "components/assets/Icons";
import useOnClickOutside from "utils/useOnClickOutside";

const BlacklistRow = ({
  entry,
  onEdit,
  onDelete,
  hasWriteAccess,
  isHovered,
  onMouseEnter,
  onMouseLeave
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const optionsRef = useRef();

  useOnClickOutside(optionsRef, () => setShowOptions(false));

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = now.getTime() - date.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 1) return "Today";
      if (diffDays <= 2) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      return "Invalid date";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'contact':
        return '👤';
      case 'domain':
        return '🌐';
      default:
        return '❓';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'contact':
        return styles.contactBadge;
      case 'domain':
        return styles.domainBadge;
      default:
        return styles.unknownBadge;
    }
  };

  const handleEdit = () => {
    if (hasWriteAccess && onEdit) {
      onEdit(entry);
    }
    setShowOptions(false);
  };

  const handleDelete = () => {
    if (hasWriteAccess && onDelete) {
      onDelete(entry);
    }
    setShowOptions(false);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <div 
      className={`${tableStyles.row} ${hasWriteAccess ? tableStyles.withActions : tableStyles.withoutActions}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Type Column */}
      <div className={tableStyles.typeCell}>
        <div className={`${styles.typeBadge} ${getTypeBadgeClass(entry.blacklist_type)}`}>
          <span className={styles.typeIcon}>{getTypeIcon(entry.blacklist_type)}</span>
          <span className={styles.typeText}>
            {entry.blacklist_type === 'contact' ? 'Contact' : 
             entry.blacklist_type === 'domain' ? 'Domain' : 
             entry.blacklist_type || 'Unknown'}
          </span>
        </div>
      </div>

      {/* Contact/Domain Column */}
      <div className={tableStyles.contactCell}>
        <div className={styles.contactInfo}>
          {entry.blacklist_type === 'contact' ? (
            <>
              <div className={styles.contactName}>{entry.contact_name || "N/A"}</div>
              <div className={styles.contactEmail}>{entry.contact_email || "N/A"}</div>
            </>
          ) : entry.blacklist_type === 'domain' ? (
            <div className={styles.domainName}>{entry.domain || "N/A"}</div>
          ) : (
            <div className={styles.unknownType}>
              {entry.blacklist_display_name || "Unknown"}
            </div>
          )}
        </div>
      </div>

      {/* Customer Column */}
      <div className={tableStyles.customerCell}>
        <div className={styles.customerName}>
          {entry.contact_customer_name || "N/A"}
        </div>
      </div>

      {/* Reason Column */}
      <div className={tableStyles.reasonCell}>
        <div 
          className={styles.reasonText}
          title={entry.reason || "No reason provided"}
        >
          {truncateText(entry.reason || "No reason provided", 60)}
        </div>
      </div>

      {/* Added By Column */}
      <div className={tableStyles.addedByCell}>
        <div className={styles.addedByText}>
          {entry.created_by_name || "Unknown"}
        </div>
      </div>

      {/* Added On Column */}
      <div className={tableStyles.dateCell}>
        <div className={styles.dateText}>
          {formatDate(entry.created_at)}
        </div>
      </div>

      {/* Actions Column */}
      {hasWriteAccess && (
        <div className={tableStyles.actionsCell}>
          <div className={tableStyles.actionsContainer}>
            <button
              className={tableStyles.optionsBtn}
              onClick={() => setShowOptions(!showOptions)}
            >
              <MoreOptionsIcon />
            </button>
            {showOptions && (
              <div className={tableStyles.options} ref={optionsRef}>
                <button
                  className={tableStyles.optionsItem}
                  onClick={handleEdit}
                >
                  Edit
                </button>
                <button
                  className={`${tableStyles.optionsItem} ${tableStyles.deleteButton}`}
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlacklistRow;